/**
 * Session Continuity Utility
 * Manages remembering and restoring user's last visited location
 */

const LAST_LOCATION_KEY = 'lastVisitedLocation';
const SESSION_EXPIRED_KEY = 'sessionExpired';

export interface LastLocation {
  pathname: string;
  search: string;
  timestamp: number;
}

/**
 * Store the current location for session continuity
 */
export const saveLastLocation = (pathname: string, search: string = '') => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;

    const location: LastLocation = {
      pathname,
      search,
      timestamp: Date.now()
    };

    localStorage.setItem(LAST_LOCATION_KEY, JSON.stringify(location));
  } catch (error) {
    console.warn('[SessionContinuity] Failed to save location:', error);
  }
};

/**
 * Get the stored last location
 */
export const getLastLocation = (): LastLocation | null => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;

    const stored = localStorage.getItem(LAST_LOCATION_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn('[SessionContinuity] Failed to get location:', error);
    return null;
  }
};

/**
 * Clear the stored last location
 */
export const clearLastLocation = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    localStorage.removeItem(LAST_LOCATION_KEY);
  } catch (error) {
    console.warn('[SessionContinuity] Failed to clear location:', error);
  }
};

/**
 * Mark that the session expired (for distinguishing from explicit logout)
 */
export const markSessionExpired = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    localStorage.setItem(SESSION_EXPIRED_KEY, 'true');
  } catch (error) {
    console.warn('[SessionContinuity] Failed to mark session expired:', error);
  }
};

/**
 * Check if the session expired (vs explicit logout)
 */
export const wasSessionExpired = (): boolean => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    return localStorage.getItem(SESSION_EXPIRED_KEY) === 'true';
  } catch (error) {
    console.warn('[SessionContinuity] Failed to check session expired:', error);
    return false;
  }
};

/**
 * Clear the session expired flag
 */
export const clearSessionExpiredFlag = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    localStorage.removeItem(SESSION_EXPIRED_KEY);
  } catch (error) {
    console.warn('[SessionContinuity] Failed to clear session expired flag:', error);
  }
};

/**
 * Get the redirect path after login
 * Returns the last location if session expired, otherwise default landing page
 */
export const getRedirectPath = (defaultPath: string = '/'): string => {
  if (wasSessionExpired()) {
    const lastLocation = getLastLocation();
    if (lastLocation) {
      // Clear the flags after use
      clearSessionExpiredFlag();
      clearLastLocation();

      // Reconstruct the full path
      const fullPath = lastLocation.search
        ? `${lastLocation.pathname}${lastLocation.search}`
        : lastLocation.pathname;

      return fullPath;
    }
  }

  return defaultPath;
};